import * as React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';
import { Button, Icon, Grid, Modal, Header } from 'semantic-ui-react';
import { hideSideBar, showSideBar } from '../../actions/navBar.actions';
import store from '../../config/store';
import './WorkoutDetails.css';
import { Workout } from '../../classes/Workout';
import { WithTranslation, withTranslation } from 'react-i18next';
import ExerciseContainer from '../exerciseCreator/view/ExerciseContainer';
import { api } from '../../config/axiosConfig';
import { formatTime } from "../../utils/Formatter";
import BrowserAlert from '../../components/BrowserAlert/BrowserAlert';
import * as bluetooth from '../../utils/bluetooth';
import { useResponsive } from '../../utils/useResponsive';

interface RouteProps {
  workout: any;
}

interface WorkoutDetailsProps extends RouteComponentProps<RouteProps>, WithTranslation {
  workout: Workout;
  dispatch: Dispatch<any>;
}

interface WorkoutDetailsState {
  workoutId?: number;
  workout?: Workout;
  photo?: any;
  confirmDeleteOpen?: boolean;
}

const WorkoutDetailsComponent: React.FC<WorkoutDetailsProps> = (props) => {
  const { isMobile, isDesktop } = useResponsive();
  const [state, setState] = React.useState<WorkoutDetailsState>({
    workoutId: undefined,
    workout: undefined,
    confirmDeleteOpen: false
  });

  React.useEffect(() => {
    store.dispatch(hideSideBar());
    let selectedWorkout = (props.location.state as any)?.workout;
    if (selectedWorkout) {
      selectedWorkout.blocGroupContainer = check0Time(selectedWorkout.blocGroupContainer);
      const photo = selectedWorkout.img ? require(`../../assets/images/workout/${selectedWorkout.img}`) : require(`../../assets/images/workout/Workout.png`);
      setState({ workout: selectedWorkout, photo });
    }
  }, [(props.location.state as any)?.workout]);

  const check0Time = (blocGroupContainer: any) => {
    return blocGroupContainer.filter((blocGroup: any) =>
      blocGroup.blocs[0].time != 0
    );
  };

  const navigate = () => {
    store.dispatch(showSideBar());
    props.history.push('/exercise-launcher');
  };

  const openConfirmDelete = () => setState(prev => ({ ...prev, confirmDeleteOpen: true }));

  const closeConfirmDelete = () => setState(prev => ({ ...prev, confirmDeleteOpen: false }));

  const deleteWorkout = () => {
    api()
      .delete(`/v1/workoutWeb/${state.workout?.id}`)
      .then(() => {
        props.history.push('/');
      })
      .catch((error) => { console.log('An error occured and the workout was not deleted') });
  };

  const edit = () => {
    if (!state.workout) return;
    const params = {
      blocGroupContainer: state.workout.blocGroupContainer,
      title: state.workout.title,
      desc: state.workout.desc,
      subtype: state.workout.subtype,
      id: state.workout.id,
      hangboard: state.workout.hangboard,
      intensityType: state.workout.intensityType
    }
    props.history.push({ pathname: `/exercise-creator/`, state: { ...params } });
  };

  const deleteButton = () => {
    if (state.workout?.type == "mine") {
      return (
        <div className='circle'>
          <span className="centerSpan">
            <Modal basic trigger={<Icon className="clickable" onClick={openConfirmDelete} name="trash alternate" />} open={state.confirmDeleteOpen} onClose={closeConfirmDelete} size='small'>
              <Header icon="trash alternate" content={props.t('workout:delete.confirmHeader')} />
              <Modal.Actions>
                <Button basic color='red' inverted onClick={closeConfirmDelete}>
                  <Icon name='remove' /> {props.t('workout:delete.cancelButton')}
                </Button>
                <Button basic color='green' inverted onClick={deleteWorkout}>
                  <Icon name='checkmark' /> {props.t('workout:delete.confirmButton')}
                </Button>
              </Modal.Actions>
            </Modal>
          </span>
        </div>
      )
    }
  };

  const editButton = () => {
    if (state.workout?.type == "mine") {
      return (
        <div className='circle'>
          <span className="centerSpan">
            <Icon className="clickable" onClick={edit} name="edit" />
          </span>
        </div>
      )
    }
  };

  if (!state.workout) return null;

  let img = null;

  const exerciseHeader = (
    <div className="exerciseHeader">
      <div>
        <h1>{props.t(state.workout.title)}</h1>
        <body>{props.t(state.workout.subtitle)}</body>
      </div>
      <div className="workoutDuration">{formatTime(state.workout.time)}</div>
    </div>
  )

  return (
    <div className='workoutDetails'>
      <div className="backgroundPicture">
        <img src={state.photo} alt="Workout" />
      </div>

      {isDesktop && (
        <>
          <div className="circle exerciseCircleDesktopOutline">
            <span className="centerSpan">
              <Icon
                className="clickable exerciseIcon"
                name="arrow left"
                onClick={navigate}
              />
            </span>
          </div>
          <div className="exerciseEditDesktopOutline">
            {editButton()}
          </div>
          <div className="exerciseDeleteDesktopOutline">
            {deleteButton()}
          </div>
          <Grid columns='equal' className="gridLayout">
            <Grid.Column className="column1" stretched>
              {exerciseHeader}
              <ExerciseContainer blocGroupContainer={state.workout.blocGroupContainer} hangboard={state.workout.hangboard} intensityType={state.workout.intensityType} />
            </Grid.Column>
            <Grid.Column className="column2">
              <div className="workoutDescriptionDesktop">{props.t(state.workout.desc)}</div>
              {!bluetooth._supported && <BrowserAlert />}
              <Button
                disabled={!bluetooth._supported}
                className="ui primary exerciseButton button"
                as={Link}
                to={{ pathname: '/exercisemanager', state: { workout: state.workout } }}
              >
                <span className="buttontext">{props.t('common:btn.start')}</span>
              </Button>
            </Grid.Column>
          </Grid>
        </>
      )}

      {isMobile && (
        <>
          <div className="circle exerciseCircleMobileOutline">
            <span className="centerSpan">
              <Icon
                className="clickable exerciseIcon"
                name="arrow left"
                onClick={navigate}
              />
            </span>
          </div>

          <div className="exerciseEditMobileOutline">
            {editButton()}
          </div>

          <div className="exerciseDeleteMobileOutline">
            {deleteButton()}
          </div>

          <Grid className="exerciseGrid">
            <Grid.Column>
              {exerciseHeader}
              <div className="workoutDescriptionMobile">{props.t(state.workout.desc)}</div>
              <ExerciseContainer blocGroupContainer={state.workout.blocGroupContainer} hangboard={state.workout.hangboard} intensityType={state.workout.intensityType} />
            </Grid.Column>
          </Grid>

          <Button
            className="ui primary exercise-bottom exerciseLaunchButton button"
            disabled={!bluetooth._supported}
            as={Link}
            to={{ pathname: '/exercisemanager', state: { workout: state.workout } }}
          >
            {!bluetooth._supported && <Icon name="exclamation triangle"></Icon>}
            <span className="buttontext">{props.t('common:btn.start')}</span>
          </Button>
        </>
      )}
    </div>
  );
};

export default withTranslation(['workout', 'common'])(WorkoutDetailsComponent);
